import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { Skeleton } from 'primeng/skeleton';

import { DisplayFormat, ListSize } from '@features/assets/components/asset-card/asset-list-item.types';

/** Poster dimensions per format/size, mirroring asset-card.scss. [width, height] in px. */
const POSTER_SIZE: Record<'landscape' | 'portrait' | 'square', Record<ListSize, [number, number]>> = {
    landscape: { xsmall: [220, 124], small: [293, 165], medium: [360, 203], large: [580, 325] },
    portrait:  { xsmall: [220, 390], small: [293, 521], medium: [360, 640], large: [450, 800] },
    square:    { xsmall: [220, 220], small: [293, 293], medium: [360, 360], large: [450, 450] },
};

/** Round-avatar diameter per size for the circle format (radio is a fixed 200px). */
const CIRCLE_SIZE: Record<ListSize, number> = { xsmall: 220, small: 293, medium: 360, large: 450 };

/**
 * Shimmer placeholder that mirrors a real `asset-list` row — a title bar plus a strip of
 * PrimeNG `p-skeleton` posters sized to match `asset-card` for the given format/size. Shown by
 * `@defer`'s `@placeholder` (and while a `catalogue-row` is still fetching) so the layout
 * reserves a row's worth of height up front; without that the IntersectionObserver behind
 * `on viewport` would see every zero-height row at once and trigger all the loads immediately.
 */
@Component({
    selector: 'catalogue-row-skeleton',
    templateUrl: './catalogue-row-skeleton.html',
    standalone: true,
    imports: [Skeleton],
    host: { class: 'block mb-15' },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CatalogueRowSkeleton {
    readonly displayFormat = input<DisplayFormat>('landscape');
    readonly size = input<ListSize>('small');
    readonly showTitle = input(true);
    readonly cardCount = input(7);
    /** Render the wide hero-shaped promo placeholder instead of a poster carousel. */
    readonly isPromo = input(false);

    protected readonly cards = computed(() => Array.from({ length: this.cardCount() }, (_, i) => i));

    /** Poster geometry for the current format/size: a circle diameter, or a rectangle w×h. */
    protected readonly poster = computed<{ width: string; height: string; circle: boolean }>(() => {
        const format = this.displayFormat();
        if (format === 'radio') return { width: '200px', height: '200px', circle: true };
        if (format === 'circle') {
            const d = `${CIRCLE_SIZE[this.size()]}px`;
            return { width: d, height: d, circle: true };
        }
        const [w, h] = POSTER_SIZE[format][this.size()];
        return { width: `${w}px`, height: `${h}px`, circle: false };
    });
}
